import React from 'react';
import { Box, Text, Flex, Tooltip, Button } from '@chakra-ui/core';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { AiFillMinusCircle, AiFillPlusCircle } from 'react-icons/ai';
import { useSelectedEngagedCandidate } from '../../../../MessagingViewContext';
import { useSeekerAllCandidates } from '../../../query/useSeekerAllCandidates';
import { Candidate, FitStatus } from '../../../../../../firebase/firestore/documents/candidate';
import { isCandidateFromFollowedPosition, isCandidateFromWatchedPosition } from '../../../../../../services/CandidateService';
import colors from '../../../../../../styles/colors';
import { useStoreState } from '../../../../../../models/hooks';
import { getTranslatedValue } from '../../../../../../utils/localizedString';
import CandidatePosition from './CandidatePosition';
import { DEFAULT_FILTER_CANDIDATE_BY_DAYS } from '../../../../../../utils/candidatesUtils';

type SeekerProps = {
  isTopCandidate?: boolean;
  // eslint-disable-next-line react/require-default-props
  selectedCandidateId?: string;
};

export enum PositionTypes {
  SCREENED = 'SCREENED',
  UNSCREENED = 'NON-SCREENED',
}

export const SeekerAllCandidates = ({ isTopCandidate, selectedCandidateId }: SeekerProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const appUserId = useStoreState((s) => s.app.user?.id);
  const filteredCandidateByDays = useStoreState((state) => state.app.accounts?.configuration?.filterCandidateByDays);
  const selectedEngagedCandidate = useSelectedEngagedCandidate();
  const candidates = useSeekerAllCandidates(selectedEngagedCandidate?.seeker || selectedCandidateId);

  const [positionType, setPositionType] = React.useState<PositionTypes>(PositionTypes.SCREENED);
  const [showAllEngaged, setShowAllEngaged] = React.useState<boolean>(false);

  const candidateFilteredByDays = filteredCandidateByDays || /* istanbul ignore next */ DEFAULT_FILTER_CANDIDATE_BY_DAYS;
  const fromDate = moment(moment().subtract(candidateFilteredByDays, 'days').format('YYYY-MM-DDT00:00:00')).toDate();

  // const recentlyEngagedCandidates = candidates?.filter(
  //   (candidate) =>
  //     candidate.monetized === true && candidate?.computedStatus?.lastUpdate && fromDate <= candidate?.computedStatus?.lastUpdate,
  // );

  const recentlyEngagedCandidates = candidates?.filter((candidate) => {
    const isMonetized = candidate?.monetized === true;
    const hasRecentUpdate = candidate?.computedStatus?.lastUpdate && fromDate <= candidate?.computedStatus?.lastUpdate;
    const isModernHirePassed = !candidate?.modernHireEnable || candidate?.modernHire?.status === 'ASSESSMENT_PASSED';

    return isMonetized && hasRecentUpdate && isModernHirePassed;
  });

  const olderEngagedCandidates = candidates?.filter(
    (candidate) =>
      candidate.monetized === true && candidate?.computedStatus?.lastUpdate && fromDate > candidate?.computedStatus?.lastUpdate,
  );

  const processingCandidates = candidates?.filter((candidate) => candidate.monetized === false);

  /* istanbul ignore next */
  const renderFollowedOrWatchedIcon = (candidate: Candidate) => {
    if (isCandidateFromFollowedPosition(candidate, appUserId)) {
      return (
        <Tooltip label={t('common:tooltip.followedPosition')} fontSize="sm">
          <CheckCircleIcon w={3} h={3} mr={2} color={colors.green['600']} />
        </Tooltip>
      );
    }
    if (isCandidateFromWatchedPosition(candidate, appUserId)) {
      return (
        <Tooltip label={t('common:tooltip.watchedPosition')} fontSize="sm">
          <CheckCircleIcon w={3} h={3} mr={2} color={colors.yellow['500']} />
        </Tooltip>
      );
    }
    return null;
  };

  const calculateAvailabilityFitBand = (availabilityFitBand: FitStatus | undefined) => {
    if (FitStatus.GOOD === availabilityFitBand) {
      return {
        icon: <AiFillPlusCircle style={{ color: colors.green['600'] }} />,
        message: t('profile.matchingRequisition.goodFit'),
      };
    }
    if (FitStatus.MEDIUM === availabilityFitBand) {
      return {
        icon: <AiFillPlusCircle style={{ color: 'orange' }} />,
        message: t('profile.matchingRequisition.mediumFit'),
      };
    }
    return {
      icon: <AiFillMinusCircle style={{ color: 'red' }} />,
      message: t('profile.matchingRequisition.poorFit'),
    };
  };

  const candidateBlock = (candidate: Candidate, dataIndex: number) => (
    <Flex
      key={candidate.id}
      alignItems="center"
      py={2}
      px={2}
      data-testid={`CandidateDropdownItem-${candidate.id}`}
      bg={dataIndex % 2 === 1 ? 'gray.150' : colors.white}
    >
      <Flex flex={1} minW={64} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Text fontSize="xs" textTransform="uppercase">
            {candidate.businessName}
          </Text>
          <Text
            fontSize="sm"
            lineHeight="shorter"
            fontWeight="bold"
            color={
              isCandidateFromFollowedPosition(candidate, appUserId) || isCandidateFromWatchedPosition(candidate, appUserId)
                ? colors.gray[800]
                : colors.gray[400]
            }
          >
            {renderFollowedOrWatchedIcon(candidate)}
            {getTranslatedValue(candidate.jobTitle, i18n.language)}
          </Text>
          {candidate.availabilityMatchedRequisitions && candidate.availabilityMatchedRequisitions.length > 0 && (
            <Flex d="inline-flex" alignItems="center" fontSize="sm" data-testid={`availability-fit-band-${candidate.id}`}>
              <Box mr={1}>{calculateAvailabilityFitBand(candidate.availabilityMatchedRequisitions[0].fit).icon}</Box>
              <Box>{calculateAvailabilityFitBand(candidate.availabilityMatchedRequisitions[0].fit).message}</Box>
            </Flex>
          )}
        </Box>
        <Box d="flex" justifyContent="flex-end" flexDirection="column" alignItems="end">
          <Text
            fontSize="xs"
            background="gray.50"
            color="#4A5568"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="10px"
            px={2}
            mb={1}
          >
            {t(`candidateStatus:${candidate.computedStatus?.status}`)}
          </Text>
          {candidate.position && !isTopCandidate && <CandidatePosition positionId={candidate.position} />}
        </Box>
      </Flex>
    </Flex>
  );

  return (
    <Box>
      <Flex alignItems="center" border={`1px solid ${colors.neutral[200]}`} borderRadius="4px" mb={2}>
        <Flex
          alignItems="center"
          justifyContent="center"
          fontWeight="700"
          fontSize="14px"
          p={1}
          w="50%"
          cursor="pointer"
          data-testid="screened-candidate"
          onClick={() => setPositionType(PositionTypes.SCREENED)}
          bg={positionType === PositionTypes.SCREENED ? colors.blue[20] : '#fff'}
          color={positionType === PositionTypes.SCREENED ? colors.blue.default : 'gray.800'}
        >
          {t('profile.screened')}
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          fontWeight="700"
          fontSize="14px"
          p={1}
          w="50%"
          cursor="pointer"
          data-testid="unscreened-candidate"
          onClick={() => setPositionType(PositionTypes.UNSCREENED)}
          bg={positionType === PositionTypes.UNSCREENED ? colors.blue[20] : '#fff'}
          color={positionType === PositionTypes.UNSCREENED ? colors.blue.default : 'gray.800'}
        >
          {t('profile.nonScreened')}
        </Flex>
      </Flex>

      {positionType === PositionTypes.SCREENED && (
        <>
          {recentlyEngagedCandidates && recentlyEngagedCandidates.map((candidate, i) => candidateBlock(candidate, i))}
          {showAllEngaged &&
            olderEngagedCandidates &&
            olderEngagedCandidates.map((candidate, i) => candidateBlock(candidate, i + (recentlyEngagedCandidates?.length || 0)))}
          {olderEngagedCandidates && olderEngagedCandidates?.length > 0 && (
            <Flex justifyContent="flex-start" mt={2}>
              <Button
                size="sm"
                onClick={() => setShowAllEngaged((prev) => !prev)}
                colorScheme="blue"
                ml={1}
                data-testid="show-more-button"
              >
                {showAllEngaged ? t('profile.showLess') : t('profile.showMore')}
              </Button>
            </Flex>
          )}
        </>
      )}

      {positionType === PositionTypes.UNSCREENED &&
        processingCandidates &&
        processingCandidates.length > 0 &&
        processingCandidates.map((candidate, i) => candidateBlock(candidate, i))}
    </Box>
  );
};

SeekerAllCandidates.defaultProps = {
  isTopCandidate: false,
};
